import { SectionCard } from '@/components/SectionCard'
import { FloorMapSkeleton } from '@/components/skeletons/FloorMapSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { Building2, Edit2, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteBuilding, useGetBuilding, useGetBuildings } from '../hooks/useBuilding'
import { Building } from '../types/building'
import { Floor } from '../types/floor'
import { BuildingDialog } from './BuildingDialog'
import { BuildingMap } from './BuildingMap'
import { FloorMap } from './FloorMap'

export function BuildingAndFloor({ page }: { page: 'floor-plan' | 'track-location' }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAdmin } = useAuth()
  const deleteBuilding = useDeleteBuilding()
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null)
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)

  const { data: configs, isLoading: isLoadingConfig } = useGetConfigs()
  const { data: buildings = [], isFetching: isFetchingBuildings } = useGetBuildings()
  const { data: building, isFetching: isFetchingBuilding } = useGetBuilding(selectedBuildingId!)

  const [buildingOpen, setBuildingOpen] = useState(false)
  const [buildingPoints, setBuildingPoints] = useState<number[][] | null>([])

  const [floors, setFloors] = useState<Floor[]>([])

  const [cancelBuildingSignal, setCancelBuildingSignal] = useState(0)

  const [mode, setMode] = useState<'create' | 'edit'>('create')

  const canManage = isAdmin
  const buildingConfig = !!configs?.[AppConfigKey.HospitalMap]

  useEffect(() => {
    setFloors(building?.floors ?? [])
  }, [building])

  const handleDeleteBuilding = async (buildingId: string) => {
    const name = buildings.find(b => b.id === buildingId)?.name
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('floorplan.building.deleteConfirmDescription', { name }),
    })

    if (confirmed) {
      setSelectedBuildingId(null)
      setSelectedBuilding(null)
      const options = getMutationOptions({
        successMessage: t('floorplan.building.toast.deleteSuccess'),
      })

      deleteBuilding.mutate(buildingId, options)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Loading */}
      {isFetchingBuilding ? (
        <FloorMapSkeleton />
      ) : (
        building && <FloorMap key={building.id} building={building} floors={floors} canManage={canManage} page={page} />
      )}

      {!buildingConfig ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {buildings.map(building => (
            <SectionCard
              key={building.id}
              data-testid="building-card"
              data-building-id={building.id}
              data-selected={selectedBuildingId === building.id}
              onClick={() => {
                setSelectedBuildingId(building.id)
              }}
              cardClass="cursor-pointer border-border/50 bg-card transition-all hover:border-primary/30"
              headerClass="pb-0"
              header={
                <div className="flex items-center gap-2">
                  <Building2 className="size-4 shrink-0 text-primary" />

                  <span
                    data-testid="building-card-name"
                    className="text-sm font-semibold text-foreground break-all line-clamp-3"
                  >
                    {building.name}
                  </span>
                </div>
              }
              right={
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1">
                    <Button
                      data-testid="building-card-edit"
                      variant={'outline'}
                      size="sm"
                      className="h-6"
                      onClick={e => {
                        e.stopPropagation()
                        setMode('edit')
                        setBuildingPoints(null)
                        setBuildingOpen(true)
                      }}
                      disabled={building.id != selectedBuildingId}
                    >
                      <Edit2 className="size-3" />
                    </Button>

                    <Badge variant="outline" className="text-[10px] text-muted-foreground h-">
                      {t('floorplan.building.floor', { count: building?.totalFloor })}
                    </Badge>
                  </div>

                  {canManage && (
                    <div className="flex justify-end">
                      <Button
                        data-testid="building-card-delete"
                        variant={'outline'}
                        size="sm"
                        className="h-6 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={e => {
                          e.stopPropagation()
                          handleDeleteBuilding(building.id)
                        }}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                  )}
                </div>
              }
            >
              <></>
            </SectionCard>
          ))}
        </div>
      ) : (
        <div>
          <BuildingMap
            name={configs?.HospitalName ?? ''}
            imageId={configs?.HospitalMap ?? ''}
            buildings={buildings}
            selectedBuildingId={selectedBuildingId}
            loadingBuilding={isFetchingBuilding}
            onSelectBuilding={id => {
              setSelectedBuildingId(id)
            }}
            canManage={canManage && page === 'floor-plan'}
            cancelSignal={cancelBuildingSignal}
            onCreateBuilding={points => {
              setMode('create')
              setSelectedBuilding(null)
              setBuildingPoints(points)
              setBuildingOpen(true)
            }}
            onEditBuilding={payload => {
              setMode('edit')
              setSelectedBuilding(payload.building)
              setBuildingPoints(payload.points ?? null)
              setBuildingOpen(true)
            }}
            onDeleteBuilding={id => {
              handleDeleteBuilding(id)
            }}
          ></BuildingMap>
        </div>
      )}

      {canManage && !buildingConfig && (
        <div className="flex">
          <Button
            data-testid="building-add-button"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              setMode('create')
              setBuildingPoints(null)
              setBuildingOpen(true)
            }}
          >
            <Plus className="size-3" />
            {t('floorplan.building.add')}
          </Button>
        </div>
      )}

      <BuildingDialog
        open={buildingOpen}
        onOpenChange={open => {
          if (!open) {
            setCancelBuildingSignal(prev => prev + 1)
          }
          setBuildingOpen(open)
        }}
        building={mode === 'edit' ? selectedBuilding : null}
        points={buildingPoints}
      ></BuildingDialog>
    </div>
  )
}
